/* eslint-disable max-classes-per-file */
import { ConditionInterpreter, Expression, TokenType } from "@evergis/condition";
import { DEFAULT_ID_ATTRIBUTE_NAME } from "../../constants";
import { createCondition, createConditionString } from "./createCondition";
import { FilterConditionOperation } from "../../../components/LayerFilter/types";
import { Style } from "../../../ducks/style/style";

class FeatureConditionError extends Error {}

/**
 * @example "1"
 */
type FeatureId = string | number;

export class FeatureCondition {
  private readonly expressions: Expression[] = [];

  constructor(condition?: string) {
    const conditionResult = new ConditionInterpreter(condition).value;

    this.expressions = conditionResult instanceof Error ? [] : conditionResult.expressions;
  }

  static fromFeatureId(featureId: FeatureId) {
    const instance = new FeatureCondition("");

    instance.add(featureId);

    return instance;
  }

  static fromStyle(style?: Style) {
    return new FeatureCondition(style && style.condition);
  }

  get valid() {
    return this.size > 0 && !this.expressions.some(expression => expression.attribute !== DEFAULT_ID_ATTRIBUTE_NAME);
  }

  get size() {
    return this.expressions.length;
  }

  add(featureId: FeatureId) {
    if (this.checkFeatureId(featureId) && !this.has(featureId)) {
      this.expressions.push({
        attribute: DEFAULT_ID_ATTRIBUTE_NAME,
        operation: TokenType.ArEq,
        value: Number(featureId),
      });
    }
  }

  remove(featureId: FeatureId) {
    if (this.has(featureId)) {
      this.expressions.splice(this.findIndex(featureId), 1);
    }
  }

  has(featureId: FeatureId) {
    return this.findIndex(featureId) > -1;
  }

  toString() {
    return this.expressions
      .map(expression => `${createConditionString(createCondition(expression))}`)
      .join(FilterConditionOperation.OR);
  }

  private checkFeatureId(featureId: FeatureId) {
    if (Number.isNaN(+featureId)) {
      throw new FeatureConditionError("wrong feature id. expected string with ability to convert to number");
    }

    return true;
  }

  private findIndex(featureId: FeatureId) {
    return this.expressions.findIndex(
      expression => expression.attribute === DEFAULT_ID_ATTRIBUTE_NAME && expression.value === +featureId,
    );
  }
}
